// Market Profile (TPO) days for the course (TpoChart.js, contentProfile.js). A day is written as its
// 30-minute periods: { open, close, periods: [[low, high], …], step } in points. Each period prints
// its letter (A, B, C…) at every price it traded, one "time price opportunity" (TPO) per price.
// tpoProfile works out what traders read from it: the point of control, the value area (70% of
// the TPOs), the initial balance (the first two periods), range extension, single prints, tails
// and poor highs and lows. dayType names the day the way profile traders do; the course's tests
// use it to check every day on a chart really is the day its lesson says it is.

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const round = (n) => Math.round(n * 1e6) / 1e6;

// `show`: only the first `show` periods (the day so far).
export function tpoProfile(day, { show } = {}) {
  const step = day.step || 1;
  const periods = day.periods.slice(0, show || day.periods.length);
  if (!periods.length) return null;
  const low = Math.min(...periods.map((p) => p[0]));
  const high = Math.max(...periods.map((p) => p[1]));
  const count = Math.round((high - low) / step) + 1;
  // Rows low to high: the letters that traded at each price.
  const rows = Array.from({ length: count }, (_, k) => {
    const price = round(low + k * step);
    const letters = periods.map((p, j) => (price >= p[0] - 1e-9 && price <= p[1] + 1e-9 ? LETTERS[j] : '')).join('');
    return { price, letters, n: letters.length };
  });

  // The point of control: the longest row, the one nearest the middle of the range on a tie.
  const mid = (low + high) / 2;
  let poc = 0;
  rows.forEach((r, k) => {
    if (r.n > rows[poc].n || (r.n === rows[poc].n && Math.abs(r.price - mid) < Math.abs(rows[poc].price - mid))) poc = k;
  });

  // The value area: from the point of control, add the two rows above or the two below, whichever
  // holds more TPOs, until 70% of all the TPOs are inside.
  const total = rows.reduce((s, r) => s + r.n, 0);
  let a = poc;
  let z = poc;
  let inside = rows[poc].n;
  const sum = (from, to) => rows.slice(Math.max(0, from), Math.min(count, to + 1)).reduce((s, r) => s + r.n, 0);
  while (inside < total * 0.7 && (a > 0 || z < count - 1)) {
    const up = z < count - 1 ? sum(z + 1, z + 2) : -1;
    const down = a > 0 ? sum(a - 2, a - 1) : -1;
    if (up >= down) { inside += up; z = Math.min(count - 1, z + 2); } else { inside += down; a = Math.max(0, a - 2); }
  }

  // The initial balance: the range of the first two periods (the first hour).
  const ib = periods.slice(0, 2);
  const ibLow = Math.min(...ib.map((p) => p[0]));
  const ibHigh = Math.max(...ib.map((p) => p[1]));

  // Single prints: runs of rows with one TPO. At the top or bottom of the day (2+ rows) they're a
  // tail (excess); inside the day they separate two distributions.
  const runs = [];
  rows.forEach((r, k) => {
    if (r.n !== 1) return;
    const last = runs[runs.length - 1];
    if (last && last.to === k - 1) last.to = k; else runs.push({ from: k, to: k });
  });
  const span = (run) => ({ low: rows[run.from].price, high: rows[run.to].price, rows: run.to - run.from + 1 });
  const bottomRun = runs.find((r) => r.from === 0);
  const topRun = runs.find((r) => r.to === count - 1);
  const buyingTail = bottomRun && bottomRun.to - bottomRun.from + 1 >= 2 ? span(bottomRun) : null;
  const sellingTail = topRun && topRun.to - topRun.from + 1 >= 2 ? span(topRun) : null;
  const singles = runs.filter((r) => r !== bottomRun && r !== topRun).map(span);

  return {
    step, rows, low, high, open: day.open, close: day.close, periods: periods.length,
    poc: rows[poc].price, valueLow: rows[a].price, valueHigh: rows[z].price, tpos: total,
    ibLow, ibHigh, extendedUp: high > ibHigh, extendedDown: low < ibLow,
    buyingTail, sellingTail, singles,
    // A high or low with two or more TPOs and no tail: an unfinished auction, likely to be revisited.
    poorHigh: rows[count - 1].n >= 2, poorLow: rows[0].n >= 2,
  };
}

// The day type, from the finished profile:
// - normal: a wide first hour that holds the whole day (or nearly)
// - normalVariation: one side extends the range, up to about twice the first hour
// - trend: one side extends far, the day closes near that extreme
// - doubleDistribution: two areas of value separated by single prints
// - neutralCenter / neutralExtreme: the range extends on both sides; closes in the middle or at an end
// - nonTrend: a narrow, quiet day with no range extension
export function dayType(p, { typicalRange = 20 } = {}) {
  const range = p.high - p.low;
  const ib = Math.max(p.ibHigh - p.ibLow, p.step);
  const atTop = p.close >= p.high - range * 0.25;
  const atBottom = p.close <= p.low + range * 0.25;
  if (p.extendedUp && p.extendedDown) return atTop || atBottom ? 'neutralExtreme' : 'neutralCenter';
  if (!p.extendedUp && !p.extendedDown) return range < typicalRange * 0.5 ? 'nonTrend' : 'normal';
  const ext = p.extendedUp ? p.high - p.ibHigh : p.ibLow - p.low;
  if (p.singles.some((s) => s.rows >= 3)) return 'doubleDistribution';
  const closesAtExtreme = p.extendedUp ? atTop : atBottom;
  if (ext >= ib * 1.5 && closesAtExtreme) return 'trend';
  if (ext <= ib * 0.25) return 'normal';
  return 'normalVariation';
}

// The profile's shape: P (the business done high, a thin tail below: short covering or buyers),
// b (the opposite: long liquidation or sellers), D (balanced, value in the middle).
export function profileShape(p) {
  const at = (p.poc - p.low) / Math.max(p.high - p.low, p.step);
  return at > 0.6 ? 'P' : at < 0.4 ? 'b' : 'D';
}

// Where one day's value sits compared with the day before.
export function valueRelation(today, before) {
  if (today.valueLow > before.valueHigh) return 'higher';
  if (today.valueHigh < before.valueLow) return 'lower';
  if (today.valueLow >= before.valueLow && today.valueHigh <= before.valueHigh) return 'inside';
  if (today.valueLow < before.valueLow && today.valueHigh > before.valueHigh) return 'outside';
  return today.poc >= before.poc ? 'overlappingHigher' : 'overlappingLower';
}
